/**
 * Onboarding v2 step 2 and 3 views (screenshots/101 ward, 104 office, 105 advocacy, 102 follow). State lives in
 * onboardingReducer; Onboarding.tsx owns the flow, the pass and the submit.
 */
import { useEffect, useMemo, useRef, useState, type Dispatch } from "react";
import { Check, ChevronsUpDown, CircleCheck, MapPin, Plus, Search } from "lucide-react";
import { Dropdown } from "../components/shared/Dropdown";
import { Face } from "../components/shared/Face";
import { Switch } from "../components/shared/ui";
import { TOPICS } from "../account/logic";
import type { AgendaMatches } from "../account/types";
import { topicPin } from "../auth/civicPass";
import { PinIcon } from "../auth/pinIcons";
import { CodeBoxes } from "../auth/AuthLayout";
import { WARDS_URL } from "../geo/wards";
import { orgForQuery, type OnboardingAction, type OnboardingState } from "./onboardingFlow";
import { wardPaths, type WardGeo } from "./wardMap";

/** The design's topic order (102): three columns. Labels are our TOPICS (account/logic.ts). */
const ORDER = ["Housing", "Renters' rights", "Street vending", "Parking", "Public safety", "Budget & taxes", "Schools", "Transit", "Immigration", "Environment", "Small business", "Zoning"];
export const TOPIC_LABELS = [...ORDER.filter((l) => TOPICS.some((t) => t.label === l)), ...TOPICS.map((t) => t.label).filter((l) => !ORDER.includes(l))];
export const shown = (label: string) => label.replace(/'/g, "’");
export const monthDay = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

let geo: Promise<WardGeo | null> | null = null;
const wardGeo = () => (geo ??= fetch(WARDS_URL).then((r) => (r.ok ? r.json() : null)).catch(() => null));

/** 101: address → ward, entirely in the browser (the address never leaves it). */
export function WardStep({ s, dispatch, found, error, onFind }: {
  s: OnboardingState; dispatch: Dispatch<OnboardingAction>; found: { photoUrl?: string; neighborhood?: string }; error: string | null; onFind: () => void;
}) {
  const [g, setG] = useState<WardGeo | null>(null);
  useEffect(() => { if (s.ward) void wardGeo().then(setG); }, [s.ward]);
  const map = useMemo(() => (g ? wardPaths(g, 128, 152) : []), [g]);
  return (<>
    <h1 className="auth__title" tabIndex={-1}>Where do you live?</h1>
    <p className="auth__sub">Only to find your ward. Never shown to anyone.</p>
    <form className="ob-address" data-found={s.ward ? "" : undefined} data-error={error ? "" : undefined} onSubmit={(e) => { e.preventDefault(); onFind(); }}>
      <MapPin size={20} aria-hidden />
      <input value={s.address} onChange={(e) => dispatch({ t: "address", address: e.target.value })} onBlur={onFind}
        placeholder="Street address in Chicago" aria-label="Street address" autoComplete="street-address" aria-invalid={error ? true : undefined} aria-describedby={error ? "ob-address-err" : undefined} />
      {s.ward && <CircleCheck size={20} className="ob-address__ok" aria-label="Found" />}
    </form>
    {error && <small id="ob-address-err" className="auth__error">{error}</small>}
    {s.ward && (
      <div className="ob-ward">
        <svg className="ob-ward__map" viewBox="0 0 128 152" width={128} height={152} role="img" aria-label={`Map of Chicago with Ward ${s.ward.number} highlighted`}>
          {map.map((w) => <path key={w.ward} d={w.d} data-on={w.ward === s.ward!.number || undefined} />)}
        </svg>
        <div className="ob-ward__who">
          <span className="ob-eyebrow is-red">You’re in Ward {s.ward.number}</span>
          <span className="ob-ward__ald"><Face name={s.ward.alderpersonName} photoUrl={found.photoUrl} size={48} />
            <span><b>Ald. {s.ward.alderpersonName}</b><small>Your alderperson{found.neighborhood ? ` · ${found.neighborhood}` : ""}</small></span></span>
        </div>
      </div>
    )}
    <div className="ob-switch">
      <Switch on={s.countInWardTotals} onChange={(on) => dispatch({ t: "countInWardTotals", on })} label={`Count my answers in Ward ${s.ward?.number ?? ""}’s totals`} />
      <span><b>Count my answers in Ward {s.ward?.number ?? "…"}’s totals</b><small>Your name is never shared.</small></span>
    </div>
  </>);
}

export interface Office { ward: number; alderpersonId: string; alderpersonName: string; photoUrl: string | null; approver: string | null }

/** 104: the City email's 6-digit code (when the account email is the City email) and which office. */
export function OfficeStep({ s, dispatch, offices, code, setCode, wait, codeSent, codeError, onResend }: {
  s: OnboardingState; dispatch: Dispatch<OnboardingAction>; offices: Office[]; code: string; setCode: (c: string) => void; wait: number; codeSent: boolean; codeError: string | null; onResend: () => void;
}) {
  const office = offices.find((o) => o.ward === s.ward?.number) ?? null;
  return (<>
    <h1 className="auth__title" tabIndex={-1}>Confirm your office.</h1>
    {codeSent ? (<>
      <p className="auth__sub">Enter the code we sent to {s.workEmail}.</p>
      <CodeBoxes value={code} onChange={setCode} label="6-digit code" autoFocus />
      {codeError && <small className="auth__error" role="alert">{codeError}</small>}
      {wait > 0 ? <small className="ob-muted">Resend in 0:{String(wait).padStart(2, "0")}</small> : <button type="button" className="ob-resend" onClick={onResend}>Resend</button>}
    </>) : (
      <div className="auth__field"><label htmlFor="ob-work">Email</label>
        <span className="auth__input"><input id="ob-work" type="email" value={s.workEmail} placeholder="you@cityofchicago.org" onChange={(e) => dispatch({ t: "workEmail", email: e.target.value })} /></span></div>
    )}
    <div className="auth__field ob-office">
      <label htmlFor="ob-office">Which office?</label>
      <span className="ob-select">
        {office && <Face name={office.alderpersonName} photoUrl={office.photoUrl} size={28} />}
        <Dropdown id="ob-office" className="ob-select__trigger" value={s.ward ? String(s.ward.number) : ""} chevron={<ChevronsUpDown size={18} aria-hidden />}
          onChange={(v) => {
            const o = offices.find((x) => x.ward === Number(v));
            dispatch({ t: "ward", ward: o ? { number: o.ward, alderpersonId: o.alderpersonId, alderpersonName: o.alderpersonName } : null });
          }}
          options={offices.map((o) => ({ value: String(o.ward), label: `Ward ${o.ward}${o.alderpersonName ? ` · Ald. ${o.alderpersonName}` : ""}` }))} />
      </span>
      {office?.approver && <small className="ob-muted">{office.approver} approves new members.</small>}
    </div>
  </>);
}

export interface OrgHit { kind: "team" | "other"; id?: string; slug?: string; name: string; members?: number }

/** 105: find your organization. A team that's here: request to join; one that isn't: ask for it. */
export function AdvocacyStep({ s, dispatch }: { s: OnboardingState; dispatch: Dispatch<OnboardingAction> }) {
  const [q, setQ] = useState(s.org?.name ?? "");
  const [hits, setHits] = useState<OrgHit[]>([]);
  // "Create a new organization": the search field becomes the new team's name.
  const [creating, setCreating] = useState(s.org?.mode === "create");
  const input = useRef<HTMLInputElement>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setHits([]); return; }
    timer.current = window.setTimeout(() => {
      void fetch(`/api/pass/orgs?q=${encodeURIComponent(q.trim())}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d: { teams: { id: string; name: string; members: number }[]; others: { slug: string; name: string }[] } | null) => {
        if (d) setHits([...d.teams.map((t) => ({ kind: "team" as const, ...t })), ...d.others.map((o) => ({ kind: "other" as const, ...o }))]);
      }).catch(() => {});
    }, 200);
  }, [q]);
  const pick = (h: OrgHit) => { setCreating(false); setQ(h.name); dispatch({ t: "org", org: h.kind === "team" ? { mode: "join", name: h.name, id: h.id } : { mode: "create", name: h.name } }); };
  // Typing after a choice: creating renames the new team; otherwise the old choice no longer matches, so it's cleared.
  const type = (v: string) => { setQ(v); dispatch({ t: "org", org: orgForQuery(s.org, v, creating) }); };
  const create = () => { setCreating(true); dispatch({ t: "org", org: orgForQuery(null, q, true) }); input.current?.focus(); };
  return (<>
    <h1 className="auth__title" tabIndex={-1}>Who do you work with?</h1>
    <span className="ob-search"><Search size={20} aria-hidden />
      <input ref={input} value={q} onChange={(e) => type(e.target.value)} aria-label="Organization" autoComplete="organization" /></span>
    {hits.length > 0 && (
      <ul className="ob-orgs" role="listbox" aria-label="Organizations">
        {hits.map((h) => {
          const on = s.org?.name === h.name;
          return (
            <li key={h.id ?? h.slug ?? h.name} role="option" aria-selected={on} tabIndex={0} onClick={() => pick(h)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(h); } }}>
              <span className="ob-orgs__mark" aria-hidden>{h.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}</span>
              <span><b>{h.name}</b><small>{h.kind === "team" ? `${h.members} member${h.members === 1 ? "" : "s"}` : "Not on here yet"}</small></span>
              <span className="ob-orgs__act">{h.kind === "team" ? "Request to join" : "Start this team"}</span>
            </li>
          );
        })}
      </ul>
    )}
    <button type="button" className="ob-create" aria-pressed={creating} onClick={create}><Plus size={18} aria-hidden />Create a new organization</button>
  </>);
}

/** 102: topics as pin tiles, then items on the next agenda that match. */
export function FollowStep({ s, dispatch, agenda }: { s: OnboardingState; dispatch: Dispatch<OnboardingAction>; agenda: AgendaMatches }) {
  return (<>
    <h1 className="auth__title" tabIndex={-1}>What do you follow?</h1>
    <p className="auth__sub">Pick a few. We’ll tell you when they move.</p>
    <div className="pin-picker" role="group" aria-label="Topics">
      {TOPIC_LABELS.map((t) => {
        const pin = topicPin(t), on = s.topics.includes(t);
        return (
          <button key={t} type="button" aria-pressed={on} onClick={() => dispatch({ t: "toggleTopic", topic: t })}>
            <span className={`pin is-mini${on ? " is-on" : ""}`} style={on ? { background: pin.color } : { color: pin.color }}><PinIcon name={pin.icon} size={12} /></span>{shown(t)}
          </button>
        );
      })}
    </div>
    {agenda.items.length > 0 && (<>
      <span className="ob-eyebrow">Up for a vote on {monthDay(agenda.meetingDate)} that match</span>
      <ul className="ob-matches">
        {agenda.items.map((i) => {
          const on = s.follow.includes(i.recordId);
          return (
            <li key={i.recordId}><span><b>{i.title}</b><small>{i.recordId}</small></span>
              <button type="button" className="ob-follow" aria-pressed={on} onClick={() => dispatch({ t: "toggleFollow", recordId: i.recordId })}>
                {on ? <Check size={16} aria-hidden /> : <Plus size={16} aria-hidden />}{on ? "Following" : "Follow"}</button></li>
          );
        })}
      </ul>
    </>)}
  </>);
}
